import { addDays, format, isAfter, isBefore, isSameDay } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { CalendarDayInterface, CalendarDayRange } from 'further-types/utils';
import { dateToLabel } from './dates';

class CalendarDay implements CalendarDayInterface {
  public readonly date: string;

  public constructor(calendarDay?: string | Date) {
    if (!calendarDay) {
      this.date = format(new Date(), 'yyyy-MM-dd');
    } else if (calendarDay instanceof Date) {
      this.date = format(calendarDay, 'yyyy-MM-dd');
    } else {
      this.date = calendarDay;
    }
  }

  public static getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public getYear() {
    const dateParts = this.date.split('-');
    return Number(dateParts[0]);
  }

  public getDay() {
    const dateParts = this.date.split('-');
    return Number(dateParts[2]);
  }

  public getMonth() {
    const dateParts = this.date.split('-');
    return Number(dateParts[1]);
  }

  public toString() {
    return this.date;
  }

  toJSON() {
    return {
      class: 'CalendarDay',
      date: this.date,
    };
  }

  public toLabel(fallback = 'N/A', format?: string) {
    return dateToLabel(this.toUTCDate(), fallback, false, true, format, true);
  }

  public toReadable(useFullMonthName = false) {
    return this.toLabel(
      '--',
      useFullMonthName ? 'MMMM d, yyyy' : 'MMM d, yyyy',
    );
  }

  public getFullMonthName() {
    return this.toLabel('--', 'MMMM');
  }

  public endOfDay() {
    return new Date(
      Date.UTC(
        this.getYear(),
        this.getMonth() - 1,
        this.getDay(),
        23,
        59,
        59,
        999,
      ),
    );
  }

  public startOfDay() {
    return this.toUTCDate();
  }

  public toUTCDate() {
    return new Date(
      Date.UTC(this.getYear(), this.getMonth() - 1, this.getDay(), 0, 0, 0, 0),
    );
  }

  public toLocalDateLabel() {
    const browserTimezone = CalendarDay.getBrowserTimezone();
    const date = toDate(this.date, {
      timeZone: browserTimezone,
    });
    return format(date, 'yyyy-MM-dd');
  }

  public toLocalDate() {
    return new Date(
      this.getYear(),
      this.getMonth() - 1,
      this.getDay(),
      0,
      0,
      0,
      0,
    );
  }

  public isAfter(date: CalendarDay | Date) {
    const calendarDayDate =
      date instanceof CalendarDay ? date : new CalendarDay(date);

    return isAfter(this.startOfDay(), calendarDayDate.startOfDay());
  }

  public isBefore(date: CalendarDay | Date) {
    const calendarDayDate =
      date instanceof CalendarDay ? date : new CalendarDay(date);

    return isBefore(this.startOfDay(), calendarDayDate.startOfDay());
  }

  public isSameDay(date: CalendarDay | Date) {
    const calendarDayDate =
      date instanceof CalendarDay ? date : new CalendarDay(date);

    return isSameDay(this.startOfDay(), calendarDayDate.startOfDay());
  }

  public isSameOrAfter(date: CalendarDay | Date) {
    return this.isAfter(date) || this.isSameDay(date);
  }

  public isSameOrBefore(date: CalendarDay | Date) {
    return this.isBefore(date) || this.isSameDay(date);
  }

  public isWithinRange(range: CalendarDayRange) {
    return (
      this.isSameOrAfter(range.startDate) && this.isSameOrBefore(range.endDate)
    );
  }

  public isInFuture() {
    return this.isAfter(new CalendarDay(new Date()));
  }

  public plusDays(days: number): CalendarDay {
    return new CalendarDay(addDays(this.toUTCDate(), days));
  }
}

export default CalendarDay;
